import { memo, useId } from "react";
import {
  getWorldCupFlagSvgXmlByCountryCode,
  resolveWorldCupCodePanelColor,
} from "@sportex100/shared/utils/worldCupFlags";

export type TeamCodeBadgeVariant = "rail" | "split" | "ring" | "stripe" | "worldCupFlag";

type TeamCodeBadgeProps = {
  teamCode?: string;
  code?: string;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  size?: number;
  textSize?: number;
  variant?: TeamCodeBadgeVariant;
  flagCountryCode?: string | null;
};

function parseHexChannel(hex: string) {
  return Number.parseInt(hex, 16);
}

function normalizeColorValue(color?: string | null): string | undefined {
  if (!color) return undefined;
  const trimmed = color.trim();
  if (!trimmed) return undefined;
  if (trimmed.startsWith("#")) return trimmed;
  if (/^[A-Fa-f0-9]{3}$/.test(trimmed) || /^[A-Fa-f0-9]{6}$/.test(trimmed) || /^[A-Fa-f0-9]{8}$/.test(trimmed)) {
    return `#${trimmed}`;
  }
  return trimmed;
}

function getColorLuminance(color: string): number {
  const normalized = color.trim();
  const hexMatch = normalized.match(/^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/);
  if (!hexMatch) return 0.15;

  let hex = hexMatch[1];
  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  }
  if (hex.length === 8) {
    hex = hex.slice(0, 6);
  }

  const r = parseHexChannel(hex.slice(0, 2)) / 255;
  const g = parseHexChannel(hex.slice(2, 4)) / 255;
  const b = parseHexChannel(hex.slice(4, 6)) / 255;

  const toLinear = (channel: number) =>
    channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;

  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

function getFontSizeForCode(code: string) {
  const len = code.length;
  if (len <= 2) return 42;
  if (len === 3) return 36;
  if (len === 4) return 31;
  return 27;
}

const TEXT_REFERENCE_BADGE_SIZE = 40;
const TEAM_CODE_FONT_FAMILY = "OswaldBoldItalic";
const RAIL_PRIMARY_TONE_MAP: Record<string, string> = {
  NSH: "#E0A000",
  VGK: "#9C824D",
  UTA: "#5FA3CF",
};
const WORLD_CUP_FLAG_X = 0;
const WORLD_CUP_FLAG_WIDTH = 100;
const WORLD_CUP_FLAG_HEIGHT = 100;
const WORLD_CUP_FLAG_Y = 0;
const WORLD_CUP_CODE_PANEL_HEIGHT = 30;
const WORLD_CUP_SEPARATOR_HEIGHT = 1;
const WORLD_CUP_SEPARATOR_COLOR = "rgba(255, 255, 255, 0.14)";
const WORLD_CUP_CODE_TEXT_SIZE = 20;
const WORLD_CUP_CODE_TEXT_VERTICAL_OFFSET_RATIO = 0.08;

function normalizeFlagCountryCode(countryCode?: string | null): string | null {
  const normalized = (countryCode ?? "").trim().toLowerCase();
  return normalized || null;
}

function toSvgDataUri(svgXml: string): string {
  return `data:image/svg+xml;utf8,${encodeURIComponent(svgXml)}`;
}

function TeamCodeBadgeBase({
  teamCode,
  code,
  primaryColor,
  secondaryColor,
  size = 24,
  textSize,
  variant = "rail",
  flagCountryCode,
}: TeamCodeBadgeProps) {
  const normalizedCode = (teamCode ?? code ?? "").trim().toUpperCase().slice(0, 4) || "---";
  const normalizedFlagCountryCode = normalizeFlagCountryCode(flagCountryCode);
  const flagSvgXml = getWorldCupFlagSvgXmlByCountryCode(normalizedFlagCountryCode);
  const flagDataUri = flagSvgXml ? toSvgDataUri(flagSvgXml) : null;
  const effectiveVariant = variant === "worldCupFlag" && !flagDataUri ? "rail" : variant;

  const normalizedPrimary = normalizeColorValue(primaryColor);
  const normalizedSecondary = normalizeColorValue(secondaryColor);
  const mappedPrimary = normalizedPrimary ?? RAIL_PRIMARY_TONE_MAP[normalizedCode];
  const strongColor = mappedPrimary ?? normalizedSecondary ?? "#27272A";
  const strongColorLuminance = getColorLuminance(strongColor);
  const accentColor = normalizedSecondary ?? normalizedPrimary ?? "#4B5563";
  const needsContrastBoost = strongColorLuminance >= 0.3;
  const scaledTextSize = textSize
    ? (textSize / TEXT_REFERENCE_BADGE_SIZE) * 100 * 1.02
    : getFontSizeForCode(normalizedCode);

  const baseId = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const clipId = `clip-${baseId}`;
  const badgeWidth = 100;
  const badgeHeight = 100;
  const worldCupCodePanelColor = resolveWorldCupCodePanelColor(normalizedPrimary, normalizedSecondary, normalizedCode);
  const worldCupCodePanelY = badgeHeight - WORLD_CUP_CODE_PANEL_HEIGHT;
  const worldCupCodePanelHeight = badgeHeight - worldCupCodePanelY;
  const worldCupCodeTextVerticalOffset = WORLD_CUP_CODE_TEXT_SIZE * WORLD_CUP_CODE_TEXT_VERTICAL_OFFSET_RATIO;
  const cornerRadius = 18;
  const innerCornerRadius = Math.max(cornerRadius - 2, 0);
  const ringCornerRadius = Math.max(cornerRadius - 4, 0);
  const railWidth = 20;
  const separatorWidth = 6;
  const railSlant = 12;
  const railBottomX = railWidth - railSlant;
  const stripeTopLeftX = railWidth;
  const stripeBottomLeftX = railBottomX;
  const contentStartX = Math.max(stripeTopLeftX + separatorWidth, stripeBottomLeftX + separatorWidth) + 4;
  const textCenterX = effectiveVariant === "rail" ? contentStartX + (badgeWidth - contentStartX) / 2 : badgeWidth / 2;
  const renderWidth = size;
  const renderHeight = size;
  const textColor = "#FFFFFF";

  const renderVariantBackground = () => {
    switch (effectiveVariant) {
      case "worldCupFlag":
        return (
          <>
            <rect x="0" y="0" width={badgeWidth} height={badgeHeight} fill={worldCupCodePanelColor} />
            {flagDataUri ? (
              <image
                href={flagDataUri}
                x={WORLD_CUP_FLAG_X}
                y={WORLD_CUP_FLAG_Y}
                width={WORLD_CUP_FLAG_WIDTH}
                height={WORLD_CUP_FLAG_HEIGHT}
                preserveAspectRatio="xMidYMin meet"
              />
            ) : null}
            <rect
              x="0"
              y={worldCupCodePanelY}
              width={badgeWidth}
              height={worldCupCodePanelHeight}
              fill={worldCupCodePanelColor}
            />
            <rect
              x="0"
              y={worldCupCodePanelY}
              width={badgeWidth}
              height={WORLD_CUP_SEPARATOR_HEIGHT}
              fill={WORLD_CUP_SEPARATOR_COLOR}
            />
          </>
        );
      case "split":
        return (
          <>
            <rect x="0" y="0" width={badgeWidth} height={badgeHeight} fill="#202228" />
            <path d={`M0 0 L${badgeWidth} 0 L0 ${badgeHeight} Z`} fill={strongColor} opacity={0.9} />
            <path d={`M${badgeWidth} ${badgeHeight} L${badgeWidth} 0 L0 ${badgeHeight} Z`} fill={accentColor} opacity={0.45} />
            <rect
              x="2"
              y="2"
              width={badgeWidth - 4}
              height={badgeHeight - 4}
              rx={innerCornerRadius}
              ry={innerCornerRadius}
              fill="none"
              stroke="rgba(255,255,255,0.15)"
              strokeWidth="2"
            />
          </>
        );
      case "ring":
        return (
          <>
            <rect x="0" y="0" width={badgeWidth} height={badgeHeight} fill="#1E2026" />
            <rect
              x="6"
              y="6"
              width={badgeWidth - 12}
              height={badgeHeight - 12}
              rx={ringCornerRadius}
              ry={ringCornerRadius}
              fill="#262A33"
              stroke={strongColor}
              strokeWidth="8"
            />
            <rect x="14" y="14" width={badgeWidth - 28} height="8" rx="4" ry="4" fill={accentColor} opacity={0.8} />
          </>
        );
      case "stripe":
        return (
          <>
            <rect x="0" y="0" width={badgeWidth} height={badgeHeight} fill="#24262D" />
            <rect x="0" y="0" width={badgeWidth} height="18" fill={strongColor} opacity={0.95} />
            <rect x="0" y="24" width={badgeWidth} height="8" fill={accentColor} opacity={0.7} />
            <rect x="0" y={badgeHeight - 10} width={badgeWidth} height="10" fill="rgba(255,255,255,0.08)" />
          </>
        );
      case "rail":
      default:
        return (
          <>
            <rect x="0" y="0" width={badgeWidth} height={badgeHeight} fill={strongColor} />
            <path d={`M0 0 L${railWidth} 0 L${railBottomX} ${badgeHeight} L0 ${badgeHeight} Z`} fill={accentColor} />
            <path
              d={`M${stripeTopLeftX} 0 L${stripeTopLeftX + separatorWidth} 0 L${stripeBottomLeftX + separatorWidth} ${badgeHeight} L${stripeBottomLeftX} ${badgeHeight} Z`}
              fill="#FFFFFF"
            />
          </>
        );
    }
  };

  return (
    <svg width={renderWidth} height={renderHeight} viewBox={`0 0 ${badgeWidth} ${badgeHeight}`}>
      <defs>
        <clipPath id={clipId}>
          <rect x="0" y="0" width={badgeWidth} height={badgeHeight} rx={cornerRadius} ry={cornerRadius} />
        </clipPath>
      </defs>

      <g clipPath={`url(#${clipId})`}>
        {renderVariantBackground()}

        <text
          x={textCenterX}
          y={effectiveVariant === "worldCupFlag" ? worldCupCodePanelY + worldCupCodePanelHeight / 2 : badgeHeight / 2}
          dy={effectiveVariant === "worldCupFlag" ? worldCupCodeTextVerticalOffset : 0}
          fill={textColor}
          fontSize={effectiveVariant === "worldCupFlag" ? WORLD_CUP_CODE_TEXT_SIZE : scaledTextSize}
          fontWeight="700"
          fontStyle="italic"
          fontFamily={TEAM_CODE_FONT_FAMILY}
          textAnchor="middle"
          dominantBaseline="middle"
          transform={
            effectiveVariant === "worldCupFlag"
              ? undefined
              : `translate(${textCenterX}, ${badgeHeight / 2}) scale(0.86, 1.03) translate(${-textCenterX}, ${-badgeHeight / 2})`
          }
        >
          {normalizedCode}
        </text>
        {needsContrastBoost ? (
          <rect
            x="1"
            y="1"
            width={badgeWidth - 2}
            height={badgeHeight - 2}
            rx={Math.max(cornerRadius - 1, 0)}
            ry={Math.max(cornerRadius - 1, 0)}
            fill="none"
            stroke="rgba(0, 0, 0, 0.35)"
            strokeWidth="2"
            vectorEffect="non-scaling-stroke"
          />
        ) : null}
        <rect
          x="0.5"
          y="0.5"
          width={badgeWidth - 1}
          height={badgeHeight - 1}
          rx={Math.max(cornerRadius - 0.5, 0)}
          ry={Math.max(cornerRadius - 0.5, 0)}
          fill="none"
          stroke="rgba(255, 255, 255, 0.15)"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
        />
      </g>
    </svg>
  );
}

export const TeamCodeBadge = memo(TeamCodeBadgeBase);
TeamCodeBadge.displayName = "TeamCodeBadge";
